import React from 'react'
import Markdown from 'react-markdown'
import { Typography, Link } from '@mui/material'
import './Message.css'

interface MessageProps {
  message: {
    id: string
    role: 'user' | 'assistant' | 'system'
    content: string
    timestamp: Date
  }
}

// Helper function to convert plain text lists and XML to markdown format
function preprocessMarkdown(text: string): string {
  if (!text) return text
  
  // Convert inline steps/bullets to proper line breaks so they render as lists (ChatGPT-style)
  // LLM often outputs "1. X 2. Y" or "fields: - A - B" in one paragraph - split into lines
  text = text.replace(/([.:])\s+(\d+\.\s)/g, '$1\n\n$2')   // "steps: 1. X" -> "steps:\n\n1. X"
  for (let n = 1; n <= 9; n++) {
    text = text.replace(new RegExp(`([^.\\d])(\\s+)(${n}\\.\\s)`, 'g'), '$1\n\n$3')  // "X 2. Y" -> "X\n\n2. Y"
  }
  text = text.replace(/([.:])\s+(-\s)/g, '$1\n$2')        // "fields: - A" -> "fields:\n- A"
  text = text.replace(/(\.)\s+(-\s)/g, '$1\n$2')          // ". - Next" -> ".\n- Next"
  
  // Strip malformed HTML/XML (e.g. hallucinated span with xmlns from LLM)
  text = text.replace(/<\s*span\s+[^>]*xmlns[^>]*>?/gi, '')
  text = text.replace(/\s+xmlns(?::\w+)?\s*=\s*["'][^"']*["']/gi, '')
  text = text.replace(/<\s*\/\s*span\s*>/gi, '')
  
  // Strip raw markdown headers (## ### ####) - LLM should not output these; convert to plain
  text = text.replace(/^#{1,6}\s*/gm, '')
  
  // Convert XML headers to markdown
  text = text.replace(/<h1[^>]*>([\s\S]*?)<\/h1>/gi, '# $1\n\n')
  text = text.replace(/<h2[^>]*>([\s\S]*?)<\/h2>/gi, '## $1\n\n')
  text = text.replace(/<h3[^>]*>([\s\S]*?)<\/h3>/gi, '### $1\n\n')
  text = text.replace(/<h4[^>]*>([\s\S]*?)<\/h4>/gi, '#### $1\n\n')
  text = text.replace(/<h5[^>]*>([\s\S]*?)<\/h5>/gi, '##### $1\n\n')
  text = text.replace(/<h6[^>]*>([\s\S]*?)<\/h6>/gi, '###### $1\n\n')
  
  // Convert XML links to markdown
  text = text.replace(/<a[^>]*href=["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi, '[$2]($1)')
  
  // Convert XML code blocks
  text = text.replace(/<code[^>]*>([\s\S]*?)<\/code>/gi, '`$1`')
  text = text.replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gi, '```\n$1\n```')
  
  // Check if text contains XML tags - parse XML lists
  if (text.includes('<list>') || text.includes('<ul>') || text.includes('<ol>') || text.includes('<li>') || text.includes('<item>')) {
    // Convert XML lists to markdown - handle both <list><item> and <ul><li> formats
    // First handle <list><item> format
    text = text.replace(/<list[^>]*>([\s\S]*?)<\/list>/gi, (match, content) => {
      const items = content.match(/<item[^>]*>([\s\S]*?)<\/item>/gi) || []
      return items.map((item: string) => {
        const itemContent = item.replace(/<item[^>]*>|<\/item>/gi, '').trim()
        return `- ${itemContent}`
      }).join('\n')
    })
    
    // Handle <ul><li> format
    text = text.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (match, content) => {
      const items = content.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || []
      return items.map((item: string) => {
        const itemContent = item.replace(/<li[^>]*>|<\/li>/gi, '').trim()
        return `- ${itemContent}`
      }).join('\n')
    })
    
    // Handle <ol><li> format (numbered lists)
    text = text.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (match, content) => {
      const items = content.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || []
      return items.map((item: string, index: number) => {
        const itemContent = item.replace(/<li[^>]*>|<\/li>/gi, '').trim()
        return `${index + 1}. ${itemContent}`
      }).join('\n')
    })
    
    // Clean up any remaining XML tags
    text = text
      .replace(/<li[^>]*>/gi, '- ')
      .replace(/<\/li>/gi, '\n')
      .replace(/<item[^>]*>/gi, '- ')
      .replace(/<\/item>/gi, '\n')
  }
  
  // Check if text already contains markdown list syntax
  if (text.includes('\n- ') || text.includes('\n* ') || text.match(/\n\d+\.\s/)) {
    // Clean up any remaining XML tags before returning
    return text.replace(/<[^>]+>/g, '').trim()
  }
  
  // Split by lines
  const lines = text.split('\n')
  const processedLines: string[] = []
  let inList = false
  let listType: 'ul' | 'ol' | null = null
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmedLine = line.trim()
    const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : ''
    
    // Check for bullet points: starts with -, *, or • followed by space
    const bulletMatch = trimmedLine.match(/^[-*•]\s+(.+)$/)
    // Check for numbered lists: starts with number followed by . or )
    const numberedMatch = trimmedLine.match(/^\d+[.)]\s+(.+)$/)
    
    if (bulletMatch) {
      // Start unordered list if not already in one
      if (!inList || listType !== 'ul') {
        if (inList && listType === 'ol') {
          processedLines.push('') // Empty line to close previous list
        }
        inList = true
        listType = 'ul'
      }
      // Convert to markdown bullet point
      processedLines.push(`- ${bulletMatch[1]}`)
    } else if (numberedMatch) {
      // Start ordered list if not already in one
      if (!inList || listType !== 'ol') {
        if (inList && listType === 'ul') {
          processedLines.push('') // Empty line to close previous list
        }
        inList = true
        listType = 'ol'
      }
      // Extract the number and content
      const numMatch = trimmedLine.match(/^(\d+)[.)]\s+(.+)$/)
      if (numMatch) {
        processedLines.push(`${numMatch[1]}. ${numMatch[2]}`)
      }
    } else {
      // Not a list item
      if (inList) {
        // Close the list if next line is not a list item or empty
        if (trimmedLine === '' || (!nextLine.match(/^[-*•]\s+/) && !nextLine.match(/^\d+[.)]\s+/))) {
          processedLines.push('') // Empty line to close the list
          inList = false
          listType = null
        }
      }
      // Add the line as-is
      processedLines.push(line)
    }
  }
  
  // Close any open list at the end
  if (inList) {
    processedLines.push('')
  }
  
  const result = processedLines.join('\n').trim()
  
  // Clean up any remaining XML tags (catch-all) - do this last
  return result.replace(/<[^>]+>/g, '').trim()
}

export default function Message({ message }: MessageProps) {
  const isUser = message.role === 'user'
  const isSystem = message.role === 'system'

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
  
  // Preprocess content to convert plain text lists to markdown
  const processedContent = isUser ? message.content : preprocessMarkdown(message.content)

  if (isSystem) {
    return (
      <div className="message system">
        <div className="message-bubble system">
          <div className="message-text">
            <Markdown
              components={{
                p: ({ children }: any) => (
                  <Typography variant="body2" component="p" sx={{ margin: 0, marginBottom: '0.5em', '&:last-child': { marginBottom: 0 } }}>
                    {children}
                  </Typography>
                ),
                ul: ({ children }: any) => (
                  <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em', display: 'block', listStyleType: 'disc' }}>
                    {children}
                  </ul>
                ),
                ol: ({ children }: any) => (
                  <ol style={{ margin: '0.5em 0', paddingLeft: '1.5em', display: 'block', listStyleType: 'decimal' }}>
                    {children}
                  </ol>
                ),
                li: ({ children }: any) => (
                  <li style={{ margin: '0.25em 0', display: 'list-item' }}>
                    {children}
                  </li>
                ),
                a: ({ href, children }: any) => (
                  <Link
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ color: '#1976d2' }}
                  >
                    {children}
                  </Link>
                ),
                strong: ({ children }: any) => (
                  <Typography component="strong" sx={{ fontWeight: 'bold' }}>
                    {children}
                  </Typography>
                ),
                code: ({ children }: any) => (
                  <code style={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                    padding: '2px 4px', 
                    borderRadius: '3px',
                    fontSize: '0.9em',
                    fontFamily: 'monospace'
                  }}>
                    {children}
                  </code>
                ),
                pre: ({ children }: any) => (
                  <pre style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    padding: '8px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontSize: '0.9em',
                    fontFamily: 'monospace'
                  }}>
                    {children}
                  </pre>
                ),
                h1: ({ children }: any) => (
                  <Typography variant="h6" component="h1" sx={{ marginTop: '0.5em', marginBottom: '0.5em' }}>
                    {children}
                  </Typography>
                ),
                h2: ({ children }: any) => (
                  <Typography variant="subtitle1" component="h2" sx={{ marginTop: '0.5em', marginBottom: '0.5em', fontWeight: 'bold' }}>
                    {children}
                  </Typography>
                ),
                h3: ({ children }: any) => (
                  <Typography variant="subtitle2" component="h3" sx={{ marginTop: '0.5em', marginBottom: '0.5em', fontWeight: 'bold' }}>
                    {children}
                  </Typography>
                ),
              }}
            >
              {processedContent}
            </Markdown>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`message ${isUser ? 'user' : 'assistant'}`}>
      <div className="message-bubble">
        <div className="message-text">
          {isUser ? (
            // User messages: plain text (no markdown needed)
            message.content
          ) : (
            // Assistant messages: render with markdown
            <Markdown
              components={{
                p: ({ children }: any) => (
                  <Typography variant="body2" component="p" sx={{ margin: 0, marginBottom: '0.5em', '&:last-child': { marginBottom: 0 } }}>
                    {children}
                  </Typography>
                ),
                ul: ({ children }: any) => (
                  <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em', display: 'block', listStyleType: 'disc' }}>
                    {children}
                  </ul>
                ),
                ol: ({ children }: any) => (
                  <ol style={{ margin: '0.5em 0', paddingLeft: '1.5em', display: 'block', listStyleType: 'decimal' }}>
                    {children}
                  </ol>
                ),
                li: ({ children }: any) => (
                  <li style={{ margin: '0.25em 0', display: 'list-item' }}>
                    {children}
                  </li>
                ),
                a: ({ href, children }: any) => (
                  <Link
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ color: '#1976d2' }}
                  >
                    {children}
                  </Link>
                ),
                strong: ({ children }: any) => (
                  <Typography component="strong" sx={{ fontWeight: 'bold' }}>
                    {children}
                  </Typography>
                ),
                code: ({ children }: any) => (
                  <code style={{ 
                    backgroundColor: 'rgba(0, 0, 0, 0.05)', 
                    padding: '2px 4px', 
                    borderRadius: '3px',
                    fontSize: '0.9em',
                    fontFamily: 'monospace'
                  }}>
                    {children}
                  </code>
                ),
                pre: ({ children }: any) => (
                  <pre style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    padding: '8px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontSize: '0.9em',
                    fontFamily: 'monospace'
                  }}>
                    {children}
                  </pre>
                ),
                h1: ({ children }: any) => (
                  <Typography variant="h6" component="h1" sx={{ marginTop: '0.5em', marginBottom: '0.5em' }}>
                    {children}
                  </Typography>
                ),
                h2: ({ children }: any) => (
                  <Typography variant="subtitle1" component="h2" sx={{ marginTop: '0.5em', marginBottom: '0.5em', fontWeight: 'bold' }}>
                    {children}
                  </Typography>
                ),
                h3: ({ children }: any) => (
                  <Typography variant="subtitle2" component="h3" sx={{ marginTop: '0.5em', marginBottom: '0.5em', fontWeight: 'bold' }}>
                    {children}
                  </Typography>
                ),
                table: ({ children }: any) => (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5em', marginBottom: '0.5em' }}>
                    {children}
                  </table>
                ),
                th: ({ children }: any) => (
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
                    {children}
                  </th>
                ),
                td: ({ children }: any) => (
                  <td style={{ border: '1px solid #ddd', padding: '8px' }}>
                    {children}
                  </td>
                ),
              }}
            >
              {processedContent}
            </Markdown>
          )}
        </div>
        <div className="message-timestamp">{formatTime(message.timestamp)}</div>
      </div>
    </div>
  )
}

